import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { EmptyRow, PageHeader, Panel } from "@/components/common";
import { BookingsPanel, BudgetPanel, DossierSteps, RenewalsPanel, TestimonialsPanel } from "@/components/AdminFormationExtras";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUSES = ["new", "in_progress", "quote_sent", "signed", "lost"];
const COLORS = { new: "bg-amber-100 text-amber-800", in_progress: "bg-sky-100 text-sky-800", quote_sent: "bg-violet-100 text-violet-800", signed: "bg-emerald-100 text-emerald-800", lost: "bg-slate-200 text-slate-700" };
const PACK_LABEL = { freelance: "Freelance / Offshore", freezone: "Free Zone", mainland: "Mainland (DED)" };
const PRICE_KEYS = ["freelance", "freezone", "mainland", "extra_visa", "tax_registration"];

function PricesPanel() {
  const { t } = useLang();
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/public/formation/packs").then((r) => setP({ freelance: r.data.freelance.price, freezone: r.data.freezone.price, mainland: r.data.mainland.price, ...r.data.options, whatsapp: r.data.whatsapp })); }, []);
  const save = async () => {
    try { await api.put("/admin/formation/packs", { ...Object.fromEntries(PRICE_KEYS.map((k) => [k, Number(p[k])])), whatsapp: p.whatsapp }); toast.success(t("saved")); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!p) return null;
  return (
    <Panel className="mb-6 p-5" data-testid="formation-prices-panel">
      <h3 className="mb-4 font-semibold">{t("fmPrices")}</h3>
      <div className="grid gap-3 sm:grid-cols-6">
        {PRICE_KEYS.map((k) => (
          <label key={k} className="text-xs text-slate-500">{t(`fm_${k}`)}
            <Input data-testid={`fm-price-${k}`} type="number" min={0} value={p[k]} onChange={(e) => setP({ ...p, [k]: e.target.value })} className="mt-1" />
          </label>
        ))}
        <label className="text-xs text-slate-500">{t("fmWhatsapp")}
          <Input data-testid="fm-whatsapp" value={p.whatsapp} onChange={(e) => setP({ ...p, whatsapp: e.target.value })} className="mt-1" placeholder="971585682850" />
        </label>
      </div>
      <Button data-testid="fm-prices-save" size="sm" className="mt-4" onClick={save}>{t("save")}</Button>
    </Panel>
  );
}

export default function AdminFormation() {
  const { t, locale } = useLang();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/formation/requests").then((r) => setRows(r.data)); }, []);
  const update = async (r, body) => {
    try {
      const { data } = await api.patch(`/admin/formation/requests/${r.id}`, body);
      setRows(rows.map((x) => (x.id === data.id ? data : x)));
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-formation-page">
      <PageHeader title={t("formationRequests")} subtitle={t("formationRequestsSubtitle")} />
      <PricesPanel />
      <BudgetPanel />
      <BookingsPanel />
      <RenewalsPanel />
      <TestimonialsPanel />
      <Panel className="overflow-x-auto">
        <table className="data-table">
          <thead><tr><th>{t("date")}</th><th>{t("name")}</th><th>{t("fmPack")}</th><th>{t("fmActivity")}</th><th>{t("fmEstimate")}</th><th>{t("message")}</th><th>{t("status")}</th></tr></thead>
          <tbody>
            {rows.length === 0 && <EmptyRow cols={7} />}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`formation-row-${r.id}`}>
                <td className="whitespace-nowrap">{new Date(r.created_at).toLocaleDateString(locale)}{r.followup_sent_at && <div className="text-[10px] text-sky-600">{t("fmFollowedUp")}</div>}</td>
                <td><div className="font-semibold">{r.name}</div><a href={`mailto:${r.email}`} className="text-xs text-[#007A3D]">{r.email}</a><div className="text-xs text-slate-400">{r.phone}{r.nationality && ` · ${r.nationality}`}</div></td>
                <td><div className="font-medium">{PACK_LABEL[r.pack]}</div><div className="text-xs text-slate-400">{r.visas > 0 && `+${r.visas} visa `}{r.options?.includes("tax_registration") && "· VAT/CT"}</div></td>
                <td className="max-w-[180px] text-sm">{r.activity || "—"}</td>
                <td className="whitespace-nowrap font-mono">{Number(r.estimate).toLocaleString("en-US")} AED</td>
                <td className="max-w-xs whitespace-pre-wrap text-xs">{r.message || "—"}<textarea data-testid={`fm-notes-${r.id}`} defaultValue={r.notes} placeholder={t("fmNotes")} onBlur={(e) => e.target.value !== r.notes && update(r, { notes: e.target.value })} className="mt-2 w-full rounded border border-slate-200 p-1 text-xs" rows={2} /></td>
                <td>
                  <select data-testid={`fm-status-${r.id}`} value={r.status} onChange={(e) => update(r, { status: e.target.value })} className={`rounded px-2 py-1 text-xs font-semibold ${COLORS[r.status]}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{t(`fs_${s}`)}</option>)}
                  </select>
                  {(r.status === "signed" || r.dossier_token) && <DossierSteps lead={r} onChange={(d) => setRows(rows.map((x) => (x.id === d.id ? d : x)))} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
